/**
 * Device Reference Manager Component
 * Admin interface for managing device_reference_table
 * Implements soft delete pattern and dropdown validation with new value addition
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  Typography,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Cached as CachedIcon,
  Psychology as PolIcon,
  SwapHoriz as BatchUpdateIcon,
  RestartAlt as RestoreIcon,
} from '@mui/icons-material';
import InfoHint from '../common/InfoHint';
import { useCourseLabels } from '../../hooks/useCourseLabels';

interface DeviceReference {
  id: number;
  course_id?: string;
  mission_id?: string;
  entity_type?: string;
  entity_id?: string;
  vehicle_beacon_id?: string;
  phone_serial_number?: string;
  samsung_phone_number?: string;
  phone_app_id?: string;
  ptt_channel?: string;
  ptt_role?: string | null;
  wardriver_id?: string;
  drone_app_id?: string;
  watch_serial_number?: string;
  watch_app_id?: string;
  watch_apple_id?: string;
  watch_phone_number?: string;
  watch_sim?: string;
  watch_device_type?: string;
  notes?: string;
  active: boolean;
  created_at?: string;
  updated_at?: string;
}

interface FieldValues {
  [key: string]: string[];
}

interface PolSettings {
  pol_processing_enabled: boolean;
  pol_daily_enabled: boolean;
  pol_weekly_enabled: boolean;
}

const DeviceReferenceManager: React.FC = () => {
  const { courseSingular, coursePlural, coursePluralLower } = useCourseLabels();
  const [references, setReferences] = useState<DeviceReference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [refreshingCache, setRefreshingCache] = useState(false);
  
  // POL Settings state
  const [polSettings, setPolSettings] = useState<PolSettings>({
    pol_processing_enabled: false,
    pol_daily_enabled: true,
    pol_weekly_enabled: true,
  });
  const [polSettingsLoading, setPolSettingsLoading] = useState(true);
  const [polSettingsUpdating, setPolSettingsUpdating] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [currentReference, setCurrentReference] = useState<Partial<DeviceReference>>({});
  
  // Selection state for batch operations
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  
  // Batch update dialog state
  const [batchUpdateDialogOpen, setBatchUpdateDialogOpen] = useState(false);
  const [batchUpdateCourse, setBatchUpdateCourse] = useState('');
  const [batchUpdateLoading, setBatchUpdateLoading] = useState(false);
  
  // Course settings dialog state
  const [courseDialogOpen, setCourseDialogOpen] = useState(false);
  const [courses, setCourses] = useState<any[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [editingCourse, setEditingCourse] = useState<any | null>(null);
  const [showAddCourse, setShowAddCourse] = useState(false);
  const [newCourse, setNewCourse] = useState({ course_id: '', start_date: '', end_date: '', is_active: true });
  const [syncMessage, setSyncMessage] = useState<string | null>(null);
  
  // Field values for dropdowns
  const [fieldValues, setFieldValues] = useState<FieldValues>({});
  
  // Filters
  const [filterEntityType, setFilterEntityType] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  // Inactive entries are hidden by default — they only exist to keep history
  // attached. Turn this on to review or restore them.
  const [showInactive, setShowInactive] = useState(false);
  
  const baseUrl = process.env.REACT_APP_API_URL || '/api';
  
  type FormField = {
    name: string;
    label: string;
    type: string;
    multiline?: boolean;
    options?: { value: string; label: string }[];
  };

  // Field definitions
  const fields: FormField[] = [
    { name: 'course_id', label: `${courseSingular} ID`, type: 'autocomplete' },
    { name: 'mission_id', label: 'Mission ID', type: 'text' },
    { name: 'entity_type', label: 'Entity Type', type: 'autocomplete' },
    { name: 'entity_id', label: 'Entity ID', type: 'autocomplete' },
    { name: 'vehicle_beacon_id', label: 'Vehicle Beacon ID', type: 'autocomplete' },
    { name: 'phone_serial_number', label: 'Phone Serial Number', type: 'autocomplete' },
    { name: 'samsung_phone_number', label: 'Samsung Phone Number', type: 'autocomplete' },
    { name: 'phone_app_id', label: 'Phone App ID', type: 'autocomplete' },
    { name: 'wardriver_id', label: 'Wardriver / Network Survey ID', type: 'autocomplete' },
    { name: 'drone_app_id', label: 'Drone / Aircraft ID', type: 'autocomplete' },
    { name: 'watch_serial_number', label: 'Watch Serial Number', type: 'autocomplete' },
    { name: 'watch_app_id', label: 'Watch App ID', type: 'autocomplete' },
    { name: 'watch_apple_id', label: 'Watch Apple ID', type: 'autocomplete' },
    { name: 'watch_phone_number', label: 'Watch Phone Number', type: 'autocomplete' },
    { name: 'watch_sim', label: 'Watch SIM', type: 'autocomplete' },
    { name: 'watch_device_type', label: 'Watch Device Type', type: 'autocomplete' },
    { name: 'notes', label: 'Notes', type: 'text', multiline: true }
  ];
  
  // Load device references
  const loadReferences = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (filterEntityType) params.append('entity_type', filterEntityType);
      if (filterCourse) params.append('course_id', filterCourse);
      if (searchTerm) params.append('search', searchTerm);
      if (showInactive) params.append('active_only', 'false');
      
      const url = `${baseUrl}/admin/device-references?${params}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }
      
      const data = await response.json();
      setReferences(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load device references');
    } finally {
      setLoading(false);
    }
  };
  
  // Load field values for dropdowns
  const loadFieldValues = async (fieldName: string) => {
    try {
      const response = await fetch(
        `${baseUrl}/admin/device-references/field-values/${fieldName}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setFieldValues(prev => ({
          ...prev,
          [fieldName]: data.values
        }));
      }
    } catch (err) {
      console.error(`Failed to load values for ${fieldName}:`, err);
    }
  };
  
  // Load POL settings
  const loadPolSettings = async () => {
    setPolSettingsLoading(true);
    try {
      const response = await fetch(`${baseUrl}/admin/system-settings/pol-processing`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPolSettings(data);
      } else if (response.status === 404) {
        // Settings not initialized yet, try to initialize them
        await initializePolSettings();
      }
    } catch (err) {
      console.error('Failed to load POL settings:', err);
    } finally {
      setPolSettingsLoading(false);
    }
  };
  
  // Initialize POL settings
  const initializePolSettings = async () => {
    try {
      await fetch(`${baseUrl}/admin/system-settings/initialize`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });
      // Reload after init
      const response = await fetch(`${baseUrl}/admin/system-settings/pol-processing`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPolSettings(data);
      }
    } catch (err) {
      console.error('Failed to initialize POL settings:', err);
    }
  };
  
  // Update POL settings
  const updatePolSettings = async (updates: Partial<PolSettings>) => {
    setPolSettingsUpdating(true);
    setError(null);
    
    try {
      const response = await fetch(`${baseUrl}/admin/system-settings/pol-processing`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }
      
      const data = await response.json();
      setPolSettings(data);
      setSuccess('POL settings updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update POL settings');
    } finally {
      setPolSettingsUpdating(false);
    }
  };

  // Load all field values on mount
  useEffect(() => {
    fields.forEach(field => {
      if (field.type === 'autocomplete') {
        loadFieldValues(field.name);
      }
    });
    loadReferences();
    loadPolSettings();
  }, []);
  
  // Reload when filters change and clear selection
  useEffect(() => {
    setSelectedIds(new Set()); // Clear selection when filters change
    loadReferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterEntityType, filterCourse, showInactive]);
  
  // Handle create/edit
  const handleOpenDialog = (mode: 'create' | 'edit', reference?: DeviceReference) => {
    setDialogMode(mode);
    setCurrentReference(reference || {});
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setCurrentReference({});
    setError(null);
  };
  
  const handleSave = async () => {
    try {
      setError(null);
      
      // Check for duplicate entity_id when creating new record
      if (dialogMode === 'create' && currentReference.entity_id) {
        const duplicate = references.find(
          ref => ref.entity_id === currentReference.entity_id && ref.active
        );
        if (duplicate) {
          setError(`Entity ID "${currentReference.entity_id}" already exists (ID: ${duplicate.id}). Please use Edit to update the existing record.`);
          return;
        }
      }
      
      const url = dialogMode === 'create'
        ? `${baseUrl}/admin/device-references`
        : `${baseUrl}/admin/device-references/${currentReference.id}`;
      
      const method = dialogMode === 'create' ? 'POST' : 'PUT';
      const payload: Record<string, any> = { ...currentReference };
      delete payload.id;
      delete payload.active;
      delete payload.created_at;
      delete payload.updated_at;
      Object.keys(payload).forEach((key) => {
        if (payload[key] === '') payload[key] = null;
      });
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        // Try to parse error detail from JSON response
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.detail || errorText);
        } catch {
          throw new Error(errorText);
        }
      }
      
      setSuccess(dialogMode === 'create' 
        ? 'Device reference created successfully' 
        : 'Device reference updated successfully (old record deactivated, new record created)');
      
      handleCloseDialog();
      loadReferences();
      
      // Reload field values to include any new values
      fields.forEach(field => {
        if (field.type === 'autocomplete') {
          loadFieldValues(field.name);
        }
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save device reference');
    }
  };
  
  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to deactivate this device reference? This will set it to inactive.')) {
      return;
    }
    
    try {
      const response = await fetch(`${baseUrl}/admin/device-references/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }
      
      setSuccess('Device reference deactivated successfully');
      loadReferences();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete device reference');
    }
  };
  
  // Flip one entry between Active and Inactive in place. Inactive entries keep
  // their row (so historical dwells stay linked) but drop out of every
  // course / entity dropdown.
  const handleSetStatus = async (id: number, active: boolean) => {
    try {
      setError(null);
      const response = await fetch(`${baseUrl}/admin/device-references/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        },
        body: JSON.stringify({ active })
      });

      if (!response.ok) {
        const errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.detail || errorText);
        } catch {
          throw new Error(errorText);
        }
      }

      setSuccess(active
        ? 'Entry restored to Active and is selectable again'
        : 'Entry set to Inactive and removed from dropdown selections');
      loadReferences();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to change status');
    }
  };

  // Instructor radios may join any talk group; everyone else is held to their team or course.
  const handleSetPttRole = async (ref: DeviceReference, role: 'instructor' | 'member') => {
    const deviceId = ref.phone_app_id || ref.entity_id;
    if (!deviceId) return;
    try {
      setError(null);
      const response = await fetch(`${baseUrl}/ptt/devices/${encodeURIComponent(deviceId)}/role`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        },
        body: JSON.stringify({ role })
      });
      if (!response.ok) {
        const errorText = await response.text();
        let detail = errorText;
        try {
          detail = JSON.parse(errorText).detail || errorText;
        } catch {
          // plain-text error body
        }
        throw new Error(detail);
      }
      setSuccess(role === 'instructor'
        ? `${ref.entity_id || deviceId} can now use any radio channel`
        : `${ref.entity_id || deviceId} is limited to its own team`);
      loadReferences();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to change radio role');
    }
  };

  const handleFieldChange = (fieldName: string, value: any) => {
    setCurrentReference(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };
  
  // Selection handlers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(new Set(references.map(ref => ref.id)));
    } else {
      setSelectedIds(new Set());
    }
  };
  
  const handleSelectOne = (id: number) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  
  const isAllSelected = references.length > 0 && selectedIds.size === references.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < references.length;
  
  // Batch update course handler
  const handleBatchUpdateCourse = async () => {
    if (selectedIds.size === 0 || !batchUpdateCourse.trim()) return;
    
    setBatchUpdateLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${baseUrl}/admin/device-references/batch-update-course`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        },
        body: JSON.stringify({
          reference_ids: Array.from(selectedIds),
          new_course_id: batchUpdateCourse.trim()
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.detail || errorText);
        } catch {
          throw new Error(errorText);
        }
      }
      
      const result = await response.json();
      setSuccess(result.message || `Successfully updated ${result.updated_count} references`);
      
      // Reset state
      setBatchUpdateDialogOpen(false);
      setBatchUpdateCourse('');
      setSelectedIds(new Set());
      
      // Reload data
      loadReferences();
      
      // Reload field values to include any new course values
      loadFieldValues('course_id');
      
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to batch update course');
    } finally {
      setBatchUpdateLoading(false);
    }
  };

  // Load courses from API
  const loadCourses = async () => {
    setCoursesLoading(true);
    try {
      const response = await fetch(`${baseUrl}/courses`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCourses(data.courses || []);
      }
    } catch (err) {
      console.error('Failed to load courses:', err);
    } finally {
      setCoursesLoading(false);
    }
  };

  // Update course dates
  const handleUpdateCourse = async (courseId: string, updates: { start_date?: string; end_date?: string; is_active?: boolean }) => {
    try {
      const response = await fetch(`${baseUrl}/courses/${encodeURIComponent(courseId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        },
        body: JSON.stringify(updates)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      setSuccess(`${courseSingular} ${courseId} updated successfully`);
      setEditingCourse(null);
      loadCourses();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update course');
    }
  };

  const handleSyncCourses = async () => {
    setCoursesLoading(true);
    setSyncMessage(null);
    try {
      const response = await fetch(`${baseUrl}/courses/sync`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}` }
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const parts: string[] = [];
      if (data.created?.length > 0) parts.push(`Created: ${data.created.join(', ')}`);
      if (data.updated?.length > 0) parts.push(`Updated: ${data.updated.join(', ')}`);
      setSyncMessage(parts.length > 0 ? parts.join(' | ') : 'All courses already in sync');
      loadCourses();
    } catch (err: any) {
      setError(err.message || 'Sync failed');
    } finally {
      setCoursesLoading(false);
    }
  };

  const handleAddCourse = async () => {
    if (!newCourse.course_id || !newCourse.start_date || !newCourse.end_date) return;
    try {
      const response = await fetch(`${baseUrl}/courses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        },
        body: JSON.stringify(newCourse)
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${response.status}`);
      }
      setSuccess(`${courseSingular} ${newCourse.course_id} created`);
      setNewCourse({ course_id: '', start_date: '', end_date: '', is_active: true });
      setShowAddCourse(false);
      loadCourses();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to create course');
    }
  };

  // Refresh course cache (materialized view)
  const handleRefreshCourseCache = async () => {
    setRefreshingCache(true);
    setError(null);
    
    try {
      const response = await fetch(`${baseUrl}/pathfinder-postgres/refresh-course-cache`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token') || ''}`
        }
      });
      
      const data = await response.json();
      
      if (!response.ok || data.error) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      
      setSuccess(data.message || `${courseSingular} cache refreshed successfully`);
      setTimeout(() => setSuccess(null), 5000);
    } catch (err: any) {
      setError(err.message || 'Failed to refresh course cache');
    } finally {
      setRefreshingCache(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      {/* Compact Header with POL Toggle */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', md: 'center' }, 
        gap: 2,
        mb: 2,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: 1,
        borderColor: 'divider'
      }}>
        {/* Left: POL Processing Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PolIcon color={polSettings.pol_processing_enabled ? 'success' : 'disabled'} fontSize="small" />
              <FormControlLabel
                control={
                  <Switch
                    checked={polSettings.pol_processing_enabled}
                    onChange={(e) => updatePolSettings({ pol_processing_enabled: e.target.checked })}
                    disabled={polSettingsLoading || polSettingsUpdating}
                    color="success"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight="medium">
                    POL Processing {polSettings.pol_processing_enabled ? '(Active)' : '(Off)'}
                  </Typography>
                }
                sx={{ mr: 0 }}
              />
              {polSettingsLoading && <CircularProgress size={14} />}
              <InfoHint title="POL processing">
                Automatic Pattern of Life analysis. Turn OFF when no training courses are active to
                save resources.
              </InfoHint>
            </Box>
          
          {polSettings.pol_processing_enabled && (
            <>
              <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' } }} />
              <ToggleButtonGroup
                value={polSettings.pol_daily_enabled ? 'daily' : 'weekly'}
                exclusive
                onChange={(_, newValue) => {
                  if (newValue) {
                    updatePolSettings({
                      pol_daily_enabled: newValue === 'daily',
                      pol_weekly_enabled: newValue === 'weekly'
                    });
                  }
                }}
                disabled={polSettingsLoading || polSettingsUpdating}
                size="small"
                sx={{ height: 28 }}
              >
                <ToggleButton value="daily" sx={{ px: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                  Daily (2AM)
                </ToggleButton>
                <ToggleButton value="weekly" sx={{ px: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                  Weekly (Mon 3AM)
                </ToggleButton>
              </ToggleButtonGroup>
            </>
          )}
        </Box>
        
        {/* Right: Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          {selectedIds.size > 0 && (
            <Tooltip title={`Batch update course for ${selectedIds.size} selected item(s)`}>
              <Button
                variant="contained"
                color="warning"
                size="small"
                startIcon={<BatchUpdateIcon />}
                onClick={() => setBatchUpdateDialogOpen(true)}
              >
                Batch Update ({selectedIds.size})
              </Button>
            </Tooltip>
          )}
          <Tooltip title={`Manage ${courseSingular.toLowerCase()} start/end dates`}>
            <Button
              variant="outlined"
              color="info"
              size="small"
              onClick={() => {
                loadCourses();
                setCourseDialogOpen(true);
              }}
            >
              {`${courseSingular} Dates`}
            </Button>
          </Tooltip>
          <Tooltip title={`Refresh ${courseSingular.toLowerCase()} dropdown cache`}>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              startIcon={refreshingCache ? <CircularProgress size={14} /> : <CachedIcon />}
              onClick={handleRefreshCourseCache}
              disabled={refreshingCache}
            >
              Refresh Cache
            </Button>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            Add Reference
          </Button>
          <Tooltip title="Refresh table">
            <IconButton onClick={loadReferences} disabled={loading} size="small">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Entity Type</InputLabel>
              <Select
                value={filterEntityType}
                onChange={(e) => setFilterEntityType(e.target.value)}
                label="Entity Type"
              >
                <MenuItem value="">All</MenuItem>
                {fieldValues.entity_type?.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{courseSingular}</InputLabel>
              <Select
                value={filterCourse}
                onChange={(e) => setFilterCourse(e.target.value)}
                label={courseSingular}
              >
                <MenuItem value="">All</MenuItem>
                {fieldValues.course_id?.map(course => (
                  <MenuItem key={course} value={course}>{course}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} lg={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search all fields (ID, serial #, app ID, phone #, notes...)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && loadReferences()}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                endAdornment: searchTerm && (
                  <IconButton size="small" onClick={() => { setSearchTerm(''); loadReferences(); }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} lg={2}>
            <Tooltip title="Inactive entries stay in the table to keep historical data linked, but they are never offered in dropdowns.">
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={showInactive}
                    onChange={(e) => setShowInactive(e.target.checked)}
                  />
                }
                label="Show inactive"
              />
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {/* Table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={isSomeSelected}
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  inputProps={{ 'aria-label': 'select all references' }}
                />
              </TableCell>
              <TableCell>ID</TableCell>
              <TableCell>Entity Type</TableCell>
              <TableCell>Entity ID</TableCell>
              <TableCell>{courseSingular}</TableCell>
              <TableCell>PTT Team (phone)</TableCell>
              <TableCell>PTT Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <CircularProgress size={30} />
                </TableCell>
              </TableRow>
            ) : references.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  No device references found
                </TableCell>
              </TableRow>
            ) : (
              references.map((ref) => (
                <TableRow 
                  key={ref.id}
                  selected={selectedIds.has(ref.id)}
                  hover
                >
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.has(ref.id)}
                      onChange={() => handleSelectOne(ref.id)}
                      inputProps={{ 'aria-labelledby': `ref-${ref.id}` }}
                    />
                  </TableCell>
                  <TableCell>{ref.id}</TableCell>
                  <TableCell>{ref.entity_type || '-'}</TableCell>
                  <TableCell id={`ref-${ref.id}`}>{ref.entity_id || '-'}</TableCell>
                  <TableCell>{ref.course_id || '-'}</TableCell>
                  <TableCell>{ref.ptt_channel ? ref.ptt_channel.replace('team-', 'Team ') : '-'}</TableCell>
                  <TableCell>
                    {ref.active && (ref.phone_app_id || ref.entity_id) ? (
                      <Select
                        size="small"
                        variant="standard"
                        value={ref.ptt_role === 'instructor' ? 'instructor' : 'member'}
                        onChange={(e) => handleSetPttRole(ref, e.target.value as 'instructor' | 'member')}
                        inputProps={{ 'aria-label': `PTT role for ${ref.entity_id || ref.id}` }}
                      >
                        <MenuItem value="member">Member</MenuItem>
                        <MenuItem value="instructor">Instructor</MenuItem>
                      </Select>
                    ) : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ref.active ? 'Active' : 'Inactive'}
                      color={ref.active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {ref.updated_at 
                      ? new Date(ref.updated_at).toLocaleDateString()
                      : '-'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog('edit', ref)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {ref.active ? (
                      <Tooltip title="Set inactive (removes it from dropdowns, keeps history)">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(ref.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Restore to active">
                        <IconButton
                          size="small"
                          onClick={() => handleSetStatus(ref.id, true)}
                        >
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Create New Device Reference' : 'Edit Device Reference'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, mt: 1 }}>
              {error}
            </Alert>
          )}
          {dialogMode === 'edit' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Editing a field sets the old record to inactive and creates a new record with your
              changes, which preserves historical data relationships. Changing only the status
              updates this record in place.
            </Alert>
          )}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {dialogMode === 'edit' && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={currentReference.active === false ? 'inactive' : 'active'}
                    onChange={(e) => handleFieldChange('active', e.target.value === 'active')}
                  >
                    <MenuItem value="active">Active — selectable in dropdowns</MenuItem>
                    <MenuItem value="inactive">Inactive — hidden from dropdowns</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {fields.map((field) => (
              <Grid item xs={12} md={field.name === 'notes' ? 12 : 6} key={field.name}>
                {field.type === 'select' ? (
                  <FormControl fullWidth>
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                      label={field.label}
                      value={(currentReference[field.name as keyof DeviceReference] as string) || ''}
                      onChange={(e) => handleFieldChange(field.name, e.target.value)}
                    >
                      {(field.options || []).map((opt) => (
                        <MenuItem key={opt.value || 'none'} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : field.type === 'autocomplete' ? (
                  <Autocomplete
                    freeSolo
                    options={fieldValues[field.name] || []}
                    value={currentReference[field.name as keyof DeviceReference] as string || ''}
                    onChange={(_, newValue) => handleFieldChange(field.name, newValue || null)}
                    onInputChange={(_, newValue, reason) => {
                      if (reason === 'input' || reason === 'clear') {
                        handleFieldChange(field.name, newValue || null);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={field.label}
                        fullWidth
                        helperText={
                          field.name === 'wardriver_id'
                            ? 'Beluga / Network Survey serial. Same value in a phone field is treated as this wardriver, not a phone.'
                            : 'Start typing or select from existing values. New values will be saved.'
                        }
                      />
                    )}
                  />
                ) : (
                  <TextField
                    label={field.label}
                    fullWidth
                    multiline={field.multiline}
                    rows={field.multiline ? 3 : 1}
                    value={currentReference[field.name as keyof DeviceReference] || ''}
                    onChange={(e) => handleFieldChange(field.name, e.target.value)}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Batch Update Course Dialog */}
      <Dialog 
        open={batchUpdateDialogOpen} 
        onClose={() => !batchUpdateLoading && setBatchUpdateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {`Batch Update ${courseSingular}`}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, mt: 1 }}>
              {error}
            </Alert>
          )}
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            This will update the course for <strong>{selectedIds.size}</strong> selected device reference(s).
            Each record will be versioned (old record deactivated, new record created with the new course).
          </Alert>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Selected Entity IDs: {
              references
                .filter(ref => selectedIds.has(ref.id))
                .map(ref => ref.entity_id || `ID:${ref.id}`)
                .slice(0, 10)
                .join(', ')
            }
            {selectedIds.size > 10 && ` ...and ${selectedIds.size - 10} more`}
          </Typography>
          
          <Autocomplete
            freeSolo
            options={fieldValues.course_id || []}
            value={batchUpdateCourse}
            onChange={(_, newValue) => setBatchUpdateCourse(newValue || '')}
            onInputChange={(_, newValue) => setBatchUpdateCourse(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`New ${courseSingular}`}
                fullWidth
                required
                helperText="Select an existing course or type a new course name"
                autoFocus
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setBatchUpdateDialogOpen(false);
              setBatchUpdateCourse('');
            }}
            disabled={batchUpdateLoading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleBatchUpdateCourse} 
            variant="contained" 
            color="warning"
            disabled={batchUpdateLoading || !batchUpdateCourse.trim()}
            startIcon={batchUpdateLoading ? <CircularProgress size={16} /> : <BatchUpdateIcon />}
          >
            {batchUpdateLoading ? 'Updating...' : `Update ${selectedIds.size} Record(s)`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Course Settings Dialog */}
      <Dialog
        open={courseDialogOpen}
        onClose={() => setCourseDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {`${courseSingular} Settings - Manage Start/End Dates`}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />}
              onClick={handleSyncCourses} disabled={coursesLoading}>
              Sync from Devices
            </Button>
            <Button size="small" variant="contained" startIcon={<AddIcon />}
              onClick={() => setShowAddCourse(true)}>
              Add {courseSingular}
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {syncMessage && (
            <Alert severity="success" sx={{ mb: 1, mt: 1 }} onClose={() => setSyncMessage(null)}>
              {syncMessage}
            </Alert>
          )}
          {showAddCourse && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2, mt: 1 }}>
              <Typography variant="subtitle2" gutterBottom>Add New {courseSingular}</Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField size="small" label={`${courseSingular} ID`} value={newCourse.course_id} placeholder="e.g. DHOST 26-003"
                  onChange={e => setNewCourse(prev => ({ ...prev, course_id: e.target.value }))} sx={{ minWidth: 160 }} />
                <TextField size="small" type="date" label="Start Date" value={newCourse.start_date} InputLabelProps={{ shrink: true }}
                  onChange={e => setNewCourse(prev => ({ ...prev, start_date: e.target.value }))} sx={{ width: 155 }} />
                <TextField size="small" type="date" label="End Date" value={newCourse.end_date} InputLabelProps={{ shrink: true }}
                  onChange={e => setNewCourse(prev => ({ ...prev, end_date: e.target.value }))} sx={{ width: 155 }} />
                <Button size="small" variant="contained" color="success" onClick={handleAddCourse}
                  disabled={!newCourse.course_id || !newCourse.start_date || !newCourse.end_date}>
                  Create
                </Button>
                <Button size="small" onClick={() => { setShowAddCourse(false); setNewCourse({ course_id: '', start_date: '', end_date: '', is_active: true }); }}>
                  Cancel
                </Button>
              </Box>
            </Paper>
          )}
          <Alert severity="info" sx={{ mb: 2, mt: showAddCourse ? 0 : 1 }}>
            Set start and end dates for each course. Quick Launch will warn users when a course is expired.
          </Alert>
          
          {coursesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : courses.length === 0 ? (
            <Alert severity="warning">
              {`No ${coursePluralLower} found. ${coursePlural} are automatically created from the Device Reference table.`}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>{courseSingular}</strong></TableCell>
                    <TableCell><strong>Start Date</strong></TableCell>
                    <TableCell><strong>End Date</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Active</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {courses.map((course) => (
                    <TableRow key={course.course_id} sx={{ 
                      bgcolor: course.status === 'expired' ? 'rgba(244, 67, 54, 0.1)' : 
                               course.status === 'active' ? 'rgba(76, 175, 80, 0.1)' : 'inherit'
                    }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {course.course_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {editingCourse?.course_id === course.course_id ? (
                          <TextField
                            type="date"
                            size="small"
                            value={editingCourse.start_date}
                            onChange={(e) => setEditingCourse({ ...editingCourse, start_date: e.target.value })}
                            sx={{ width: 150 }}
                          />
                        ) : (
                          course.start_date
                        )}
                      </TableCell>
                      <TableCell>
                        {editingCourse?.course_id === course.course_id ? (
                          <TextField
                            type="date"
                            size="small"
                            value={editingCourse.end_date}
                            onChange={(e) => setEditingCourse({ ...editingCourse, end_date: e.target.value })}
                            sx={{ width: 150 }}
                          />
                        ) : (
                          course.end_date
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={course.status} 
                          size="small"
                          color={
                            course.status === 'active' ? 'success' :
                            course.status === 'expired' ? 'error' :
                            course.status === 'upcoming' ? 'info' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {editingCourse?.course_id === course.course_id ? (
                          <Switch
                            checked={editingCourse.is_active}
                            onChange={(e) => setEditingCourse({ ...editingCourse, is_active: e.target.checked })}
                            size="small"
                          />
                        ) : (
                          <Switch
                            checked={course.is_active}
                            disabled
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {editingCourse?.course_id === course.course_id ? (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              onClick={() => handleUpdateCourse(course.course_id, {
                                start_date: editingCourse.start_date,
                                end_date: editingCourse.end_date,
                                is_active: editingCourse.is_active
                              })}
                            >
                              Save
                            </Button>
                            <Button
                              size="small"
                              onClick={() => setEditingCourse(null)}
                            >
                              Cancel
                            </Button>
                          </Box>
                        ) : (
                          <IconButton
                            size="small"
                            onClick={() => setEditingCourse({
                              course_id: course.course_id,
                              start_date: course.start_date,
                              end_date: course.end_date,
                              is_active: course.is_active
                            })}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCourseDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceReferenceManager;
